import { motion } from 'framer-motion';
import { Hash } from 'lucide-react';
import { DiscordIcon } from '../ui/DiscordIcon';

const benefits = [
  {
    icon: DiscordIcon,
    title: 'Live support & collaboration',
    description: 'Get instant help from the community and FiverFlow team',
    color: 'from-indigo-500 to-purple-500'
  },
  {
    icon: DiscordIcon,
    title: 'Exclusive guides & resources',
    description: 'Access premium tutorials, templates, and best practices',
    color: 'from-cyan-500 to-blue-500'
  },
  {
    icon: DiscordIcon,
    title: 'Community forums',
    description: 'Share ideas, ask questions, and learn from experienced freelancers',
    color: 'from-purple-500 to-pink-500'
  },
  {
    icon: DiscordIcon,
    title: 'Portfolio showcase',
    description: 'Showcase your work and get feedback from peers',
    color: 'from-orange-500 to-red-500'
  },
  {
    icon: DiscordIcon,
    title: 'Promote your services',
    description: 'Connect with potential clients and collaborators',
    color: 'from-emerald-500 to-teal-500'
  },
  {
    icon: DiscordIcon,
    title: 'Network & grow',
    description: 'Build meaningful connections with freelancers worldwide',
    color: 'from-violet-500 to-fuchsia-500'
  }
];

// Fake Discord messages for the mockup
const mockMessages = [
  { user: 'Sarah M.', text: 'Just hit $10k this month using FiverFlow! 🎉', time: '2m ago', color: 'bg-indigo-500/20' },
  { user: 'Alex T.', text: 'The invoice templates are a game-changer', time: '5m ago', color: 'bg-purple-500/20' },
  { user: 'Maria L.', text: 'Anyone tried the new AI assistant features?', time: '8m ago', color: 'bg-cyan-500/20' },
  { user: 'David K.', text: 'Thanks for the help with client management!', time: '12m ago', color: 'bg-emerald-500/20' },
];

export const DiscordCommunitySection = () => {
  return (
    <section id="community" className="py-16 md:py-24 relative overflow-hidden">
      {/* Background glow effect */}
      <div className="absolute inset-0 -z-10">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-indigo-500/10 rounded-full blur-[100px]" />
        <div className="absolute top-1/4 right-1/4 w-[400px] h-[400px] bg-purple-500/10 rounded-full blur-[80px]" />
      </div>

      <div className="max-w-[1300px] mx-auto px-4 md:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <div className="inline-flex items-center gap-2 mb-6">
            <motion.div
              animate={{
                scale: [1, 1.1, 1],
              }}
              transition={{
                duration: 2,
                repeat: Infinity,
                ease: "easeInOut",
              }}
              className="w-12 h-12 rounded-2xl bg-gradient-to-br from-indigo-500 via-purple-500 to-cyan-400 flex items-center justify-center"
            >
              <Hash className="text-white" size={24} />
            </motion.div>
            <span className="text-indigo-400 uppercase tracking-wider text-xs font-medium">Community Hub</span>
          </div>

          <h2 className="text-3xl md:text-4xl font-semibold text-white mb-4">
            Where Freelancers Connect, Grow, and Succeed
          </h2>
          <p className="text-lg text-neutral-300 max-w-2xl mx-auto mb-2">
            Join our official Discord hub to connect with other freelancers, get support, share your portfolio, and discover exclusive resources.
          </p>
          <p className="text-base text-neutral-400 max-w-2xl mx-auto">
            Our community helps you grow, learn, and collaborate — all in one place.
          </p>
        </motion.div>

        {/* Main Content Grid */}
        <div className="grid md:grid-cols-2 gap-12 items-center mb-16">
          {/* Left: Benefits Grid */}
          <motion.div
            initial={{ opacity: 0, x: -40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="space-y-4"
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {benefits.map((benefit, index) => {
                const Icon = benefit.icon;
                return (
                  <motion.div
                    key={benefit.title}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                    className="rounded-xl border border-white/10 bg-[rgba(255,255,255,0.03)] backdrop-blur-xl p-5 hover:bg-[rgba(255,255,255,0.05)] transition-colors group"
                  >
                    <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${benefit.color} flex items-center justify-center mb-3 group-hover:scale-110 transition-transform`}>
                      <Icon className="text-white" size={20} />
                    </div>
                    <h3 className="text-white font-semibold text-sm mb-1">{benefit.title}</h3>
                    <p className="text-neutral-400 text-xs">{benefit.description}</p>
                  </motion.div>
                );
              })}
            </div>
          </motion.div>

          {/* Right: Discord Mockup */}
          <motion.div
            initial={{ opacity: 0, x: 40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="relative"
          >
            <div className="relative rounded-2xl border border-white/10 bg-[rgba(255,255,255,0.03)] backdrop-blur-xl shadow-[0_30px_120px_rgba(0,0,0,0.8)] overflow-hidden">
              {/* Discord Header */}
              <div className="bg-gradient-to-r from-indigo-500/20 via-purple-500/20 to-cyan-500/20 border-b border-white/10 p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-indigo-500 to-purple-500 flex items-center justify-center">
                    <Hash className="text-white" size={20} />
                  </div>
                  <div>
                    <h3 className="text-white font-semibold text-sm">FiverFlow Community</h3>
                    <p className="text-neutral-400 text-xs">Official Discord Server</p>
                  </div>
                </div>
              </div>

              {/* Messages */}
              <div className="p-4 space-y-3 max-h-[400px] overflow-y-auto">
                {mockMessages.map((message, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.3 + index * 0.1 }}
                    className={`flex gap-3 p-3 rounded-lg ${message.color} border border-white/5`}
                  >
                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-indigo-400 to-purple-400 flex items-center justify-center flex-shrink-0">
                      <span className="text-white text-xs font-semibold">
                        {message.user.split(' ').map(n => n[0]).join('')}
                      </span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-white font-medium text-sm">{message.user}</span>
                        <span className="text-neutral-500 text-xs">{message.time}</span>
                      </div>
                      <p className="text-neutral-300 text-sm">{message.text}</p>
                    </div>
                  </motion.div>
                ))}

                {/* Animated typing indicator */}
                <motion.div
                  animate={{ opacity: [0.5, 1, 0.5] }}
                  transition={{ duration: 1.5, repeat: Infinity }}
                  className="flex gap-2 items-center pl-11"
                >
                  <div className="flex gap-1">
                    <motion.div
                      className="w-2 h-2 rounded-full bg-neutral-500"
                      animate={{ opacity: [0.5, 1, 0.5] }}
                      transition={{ duration: 1.2, repeat: Infinity, delay: 0 }}
                    />
                    <motion.div
                      className="w-2 h-2 rounded-full bg-neutral-500"
                      animate={{ opacity: [0.5, 1, 0.5] }}
                      transition={{ duration: 1.2, repeat: Infinity, delay: 0.2 }}
                    />
                    <motion.div
                      className="w-2 h-2 rounded-full bg-neutral-500"
                      animate={{ opacity: [0.5, 1, 0.5] }}
                      transition={{ duration: 1.2, repeat: Infinity, delay: 0.4 }}
                    />
                  </div>
                  <span className="text-neutral-500 text-xs">Someone is typing...</span>
                </motion.div>
              </div>

              {/* Bottom gradient fade */}
              <div className="absolute bottom-0 left-0 right-0 h-20 bg-gradient-to-t from-black/40 to-transparent pointer-events-none" />
            </div>

            {/* Decorative elements */}
            <div className="absolute -top-2 -left-2 w-6 h-6 border-t-2 border-l-2 rounded-tl-lg opacity-60 border-indigo-500/50" />
            <div className="absolute -top-2 -right-2 w-6 h-6 border-t-2 border-r-2 rounded-tr-lg opacity-60 border-purple-500/50" />
            <div className="absolute -bottom-2 -left-2 w-6 h-6 border-b-2 border-l-2 rounded-bl-lg opacity-60 border-cyan-500/50" />
            <div className="absolute -bottom-2 -right-2 w-6 h-6 border-b-2 border-r-2 rounded-br-lg opacity-60 border-indigo-500/50" />
          </motion.div>
        </div>

        {/* CTA Button */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center"
        >
          <motion.a
            href="https://dsc.gg/fiverflow"
            target="_blank"
            rel="noopener noreferrer"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="inline-flex items-center gap-3 px-8 py-4 rounded-full bg-gradient-to-r from-indigo-500 via-purple-500 to-cyan-400 text-white font-medium shadow-[0_20px_80px_rgba(99,102,241,0.6)] hover:shadow-[0_30px_120px_rgba(99,102,241,0.9)] transition-shadow group"
          >
            <Hash className="w-5 h-5" />
            <span>Join the Community</span>
            <motion.svg
              className="w-5 h-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              animate={{ x: [0, 4, 0] }}
              transition={{ duration: 1.5, repeat: Infinity }}
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
            </motion.svg>
          </motion.a>
        </motion.div>
      </div>
    </section>
  );
};

